import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { UserMinus } from "lucide-react";
import { FaExclamationTriangle, FaUserSlash } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import "./UnmatchConfirmationModal.css";
// Shared modal styles (adjust the paths to wherever you saved these files)
// import "./Devicesessionmodals.css";
// import "./ChatModals.css";

export interface UnmatchConfirmationModalProps {
  show: boolean;
  onHide?: () => void;
  onClose?: () => void;
  onConfirm?: () => void | Promise<void>;
  submit?: () => void | Promise<void>;
  onConfirmKeep?: () => void | Promise<void>;
  submit1?: () => void | Promise<void>;
  userName?: string;
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  buttonText?: string;
  secondButtonText?: string;
  cancelText?: string;
  unmatchReason?: string;
  setUnmatchReason?: Dispatch<SetStateAction<string>>;
  unmatchReasonErr?: boolean;
  setUnmatchReasonErr?: Dispatch<SetStateAction<boolean>>;
  isUnmatching?: boolean;
  loading?: boolean;
  disabled?: boolean;
  popupType?: string;
}

const UnmatchConfirmationModal: React.FC<UnmatchConfirmationModalProps> = ({
  show,
  onHide,
  onClose,
  onConfirm,
  submit,
  onConfirmKeep,
  submit1,
  userName,
  title,
  message,
  confirmText = "Un-Match",
  buttonText,
  secondButtonText = "Unmatch & Keep Chat",
  cancelText = "Cancel",
  unmatchReason,
  setUnmatchReason,
  unmatchReasonErr,
  setUnmatchReasonErr,
  isUnmatching = false,
  loading = false,
  disabled = false,
}) => {
  const [confirmStep, setConfirmStep] = useState<"delete" | "keep" | null>(null);

  const isLoading = isUnmatching || loading;

  const handleClose = () => {
    setConfirmStep(null);
    if (onHide) onHide();
    else if (onClose) onClose();
  };

  useEffect(() => {
    if (!show) {
      setConfirmStep(null);
    }
  }, [show]);

  const candidateName =
    userName ||
    (title
      ? title.replace(/^Are you sure you want to un-?match\s+/i, "").replace(/\?$/, "").trim()
      : "this user");

  // Dual chat options ONLY enabled if onConfirmKeep or submit1 handler is explicitly passed
  const hasDualOptions = Boolean(onConfirmKeep || submit1);

  const handleOptionClick = (type: "delete" | "keep") => {
    if (setUnmatchReason && (!unmatchReason || unmatchReason.trim() === "")) {
      setUnmatchReasonErr?.(true);
      return;
    }
    setUnmatchReasonErr?.(false);
    setConfirmStep(type);
  };

  const handleFinalSubmit = async () => {
    if (confirmStep === "keep" && (onConfirmKeep || submit1)) {
      const fn = onConfirmKeep || submit1;
      if (fn) await fn();
    } else {
      const fn = onConfirm || submit;
      if (fn) await fn();
    }
  };

  const primaryBtnText = confirmText || buttonText || "Un-Match";

  const closeButton = (
    <button
      type="button"
      className="dm-close"
      onClick={handleClose}
      disabled={isLoading}
      aria-label="Close"
      title="Close modal"
    >
      <IoClose size={18} />
    </button>
  );

  // If dual options (chat mode), render full 2-step chat unmatch flow
  if (hasDualOptions) {
    return (
      <Modal
        className="ChatConfirmModal UnmatchConfirmModal"
        show={show}
        onHide={isLoading ? undefined : handleClose}
        centered
        backdrop="static"
        contentClassName="dm-content"
        backdropClassName="dm-backdrop"
      >
        {closeButton}

        {confirmStep !== null ? (
          /* ── STEP 2: Final Confirmation for Unmatch with Chat ── */
          <>
            <Modal.Body className="dm-body">
              <div className="dm-icon">
                <FaExclamationTriangle size={26} />
              </div>
              <h2 className="dm-title">
                {confirmStep === "delete" ? "Confirm Permanent Deletion" : "Confirm Unmatching Connection"}
              </h2>
              <p className="dm-text">
                {confirmStep === "delete"
                  ? `Are you sure you want to unmatch with ${candidateName} and permanently delete all conversation history? This action is irreversible.`
                  : `Are you sure you want to unmatch with ${candidateName}? Your conversation history will remain archived for reference, but messaging will be disabled.`}
              </p>
            </Modal.Body>

            <Modal.Footer className="dm-footer dm-footer-split">
              <button
                type="button"
                className="dm-btn dm-btn-cancel"
                onClick={() => setConfirmStep(null)}
                disabled={isLoading}
              >
                {cancelText}
              </button>
              <button
                type="button"
                className={`dm-btn ${confirmStep === "delete" ? "dm-btn-danger" : "dm-btn-navy"}`}
                onClick={handleFinalSubmit}
                disabled={isLoading || disabled}
              >
                {isLoading ? (
                  <>
                    <Spinner animation="border" size="sm" variant="light" />
                    <span>Processing...</span>
                  </>
                ) : confirmStep === "delete" ? (
                  "Yes, Delete & Unmatch"
                ) : (
                  "Yes, Unmatch Connection"
                )}
              </button>
            </Modal.Footer>
          </>
        ) : (
          /* ── STEP 1: Reason & Dual Chat Choice View ── */
          <>
            <Modal.Body className="dm-body">
              <div className="dm-icon">
                <FaUserSlash size={26} />
              </div>
              <h2 className="dm-title">{title || `Unmatch ${candidateName}`}</h2>
              <p className="dm-text">
                {message ||
                  `Please select how you would like to handle your chat history with ${candidateName} before unmatching.`}
              </p>

              {/* Unmatch Reason Input */}
              {setUnmatchReason && (
                <div className="dm-field">
                  <label className="dm-label">
                    Reason for unmatching <span className="dm-req">*</span>
                  </label>
                  <textarea
                    name="unmatchReason"
                    value={unmatchReason || ""}
                    onChange={(e) => {
                      setUnmatchReason?.(e.target.value);
                      setUnmatchReasonErr?.(false);
                    }}
                    placeholder="Please provide a brief reason for unmatching..."
                    className={`dm-textarea ${unmatchReasonErr ? "is-invalid" : ""}`}
                    rows={3}
                    disabled={isLoading}
                  />
                  {unmatchReasonErr && <p className="dm-field-error">Reason is required</p>}
                </div>
              )}
            </Modal.Body>

            <Modal.Footer className="dm-footer dm-footer-split">
              <button
                type="button"
                className="dm-btn dm-btn-soft"
                onClick={() => handleOptionClick("keep")}
                disabled={isLoading}
              >
                {secondButtonText}
              </button>
              <button
                type="button"
                className="dm-btn dm-btn-danger"
                onClick={() => handleOptionClick("delete")}
                disabled={isLoading}
              >
                {buttonText || "Unmatch & Delete Chat"}
              </button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    );
  }

  /* ── SIMPLE UN-MATCH MODAL (Used for Candidates & Connections without chat) ── */
  const displayTitle =
    title ||
    (candidateName !== "this user"
      ? `Are you sure you want to un-match ${candidateName}?`
      : "Are you sure you want to un-match this user?");

  const displayMessage =
    message !== undefined
      ? message
      : "This will remove the connection between you.";

  return (
    <Modal
      show={show}
      onHide={isLoading ? undefined : handleClose}
      centered
      backdrop="static"
      keyboard={!isLoading}
      contentClassName="dm-content"
      backdropClassName="dm-backdrop"
    >
      {closeButton}

      <Modal.Body className="dm-body">
        <div className="dm-icon">
          <UserMinus size={26} />
        </div>
        <h2 className="dm-title">{displayTitle}</h2>
        {displayMessage && <div className="dm-text">{displayMessage}</div>}
      </Modal.Body>

      <Modal.Footer className="dm-footer">
        <button
          type="button"
          className="dm-btn dm-btn-cancel"
          onClick={handleClose}
          disabled={isLoading}
        >
          {cancelText}
        </button>
        <button
          type="button"
          className="dm-btn dm-btn-danger"
          onClick={handleFinalSubmit}
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
              <span>Removing...</span>
            </>
          ) : (
            primaryBtnText
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UnmatchConfirmationModal;